import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import {
  Menu as MenuIcon,
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  ExitToApp,
  Help as HelpIcon,
  PersonAdd,
  Message as MessageIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import TeacherNavigation from './TeacherNavigation';
import SupportDialog from '../SupportDialog';
import InviteDialog from '../InviteDialog';
import NudgeDialog from '../NudgeDialog';
import {
  handleProfile,
  handleChangePassword,
  handleLogout
} from '../common/GeneralnNagivationHandler';

const TeacherLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [supportOpen, setSupportOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);

  // Close mobile drawer when window is resized to desktop
  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleProfileClick = () => {
    handleProfile(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleChangePasswordClick = () => {
    handleChangePassword(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleLogout(logout, setMobileOpen, navigate);
    setAnchorEl(null);
  };

  const handleSupportClick = () => {
    setSupportOpen(true);
  };

  const handleInviteClick = () => {
    setInviteOpen(true);
  };

  const handleNudgeClick = () => {
    setNudgeOpen(true);
  };

  const teacherStudentTabs = [
    { label: 'Students', path: '/teacher/students' },
    { label: 'Classes', path: '/teacher/batches' },
    { label: 'Exam Plan', path: '/teacher/exam-prep' },
    { label: 'Assignments', path: '/teacher/assignments' },
    { label: 'Recommendations', path: '/analytics/teacher-wrapper' },
  ];

  const teacherQuestionTabs = [
    { label: 'Question Bank', path: '/teacher/questionbank' },
    { label: 'Test Library', path: '/teacher/tests' },
  ];

  const getTeacherTabs = () => {
    const path = location.pathname;
    if (
      path === '/teacher/students' ||
      path.startsWith('/teacher/batches') ||
      path === '/teacher/exam-prep' ||
      path === '/teacher/assignments' ||
      path === '/teacher/analytics' ||
      path === '/analytics/teacher-wrapper'
    ) {
      return teacherStudentTabs;
    }
    if (path === '/teacher/questionbank' || path === '/teacher/tests') {
      return teacherQuestionTabs;
    }
    return [];
  };

  const getActiveTabPath = (tabs) => {
    if (!tabs.length) return false;
    if (location.pathname.startsWith('/teacher/batches')) return '/teacher/batches';
    if (location.pathname === '/teacher/analytics') return '/analytics/teacher-wrapper';
    return tabs.some((tab) => tab.path === location.pathname) ? location.pathname : false;
  };

  const teacherTabs = getTeacherTabs();
  const activeTeacherTab = getActiveTabPath(teacherTabs);

  // Get current page title
  const getPageTitle = () => {
    const isParent = user?.isParent || user?.is_parent || (user?.role_name && user?.role_name.toString().toLowerCase() === 'parent');
    
    if (location.pathname.startsWith('/teacher/batches/')) {
      return 'Batch';
    }
    const pathMap = {
      '/dashboard': 'Dashboard',
      '/teacher/students': isParent ? 'My Children' : 'Manage Students',
      '/teacher/batches': 'Manage Classes',
      '/teacher/exam-prep': 'Exam Prep Plan',
      '/teacher/questionbank': 'Question Bank',
      '/teacher/tests': 'Test Library',
      '/teacher/assignments': 'Assignments',
      '/teacher/analytics': isParent ? 'Recommendations' : 'Recommendations',
      '/analytics/teacher-wrapper': 'Recommendations',
      '/teacher/calendar': 'Calendar',
      '/teacher/awards': 'Rewards & Recognition',
      '/parent/assignments': 'Assignments',
      '/parent/awards': 'Rewards & Recognition',
      '/profile': 'My Profile',
      '/settings/change-password': 'Change Password'
    };
    return pathMap[location.pathname] || 'Dashboard';
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Navigation Drawer */}
      <TeacherNavigation
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        supportOpen={supportOpen}
        onSupportOpen={setSupportOpen}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', sm: `calc(100% - ${collapsed ? 72 : 280}px)` },
          maxWidth: '100%',
          minWidth: 0,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* App Bar */}
        <AppBar
          position="fixed"
          sx={{
            width: { xs: '100%', sm: `calc(100% - ${collapsed ? 72 : 280}px)` },
            ml: { xs: 0, sm: `${collapsed ? 72 : 280}px` },
            transition: theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1, sm: 2 },
              pr: { xs: 1, sm: 2 },
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              minWidth: 0,
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, flexShrink: 0, display: { xs: 'flex', sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden' }}>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  color: 'text.primary',
                }}
              >
                {getPageTitle()}
              </Typography>
            </Box>

            {/* Right side: Invite, Nudge, Help, Avatar - always visible, never shrink */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0 }}>
              {user && (user.isTeacher || user.is_teacher) && (
                <Tooltip title="Send Invite">
                  <IconButton
                    onClick={handleInviteClick}
                    sx={{ color: 'primary.main', '&:hover': { backgroundColor: 'primary.50' } }}
                    aria-label="Send invite"
                  >
                    <PersonAdd />
                  </IconButton>
                </Tooltip>
              )}
              {user && (user.isTeacher || user.is_teacher || user.isParent || user.is_parent) && (
                <Tooltip title="Encourage learners">
                  <IconButton
                    onClick={handleNudgeClick}
                    sx={{ color: 'secondary.main', '&:hover': { backgroundColor: 'secondary.50' } }}
                    aria-label="Encourage learners"
                  >
                    <MessageIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Need help">
                <IconButton
                  onClick={handleSupportClick}
                  sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'action.hover' } }}
                  aria-label="Need help"
                >
                  <HelpIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Account menu">
                <IconButton
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  color="inherit"
                  sx={{ p: 0.5 }}
                  aria-label="Account menu"
                >
                  <Avatar
                    sx={{
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      bgcolor: 'primary.main',
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                    }}
                    src={user?.profile_picture}
                  >
                    {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'T'}
                  </Avatar>
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  elevation: 3,
                  sx: {
                    minWidth: 220,
                    mt: 1.5,
                    maxHeight: 'calc(100vh - 100px)',
                  },
                }}
              >
                <MenuItem disabled sx={{ opacity: 0.7 }}>
                  <ListItemText
                    primary={user?.email || 'teacher@school.edu'}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: 'text.secondary',
                      sx: { fontSize: '0.875rem' },
                    }}
                  />
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleChangePasswordClick}>
                  <ListItemIcon>
                    <VpnKeyIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Change Password</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogoutClick}>
                  <ListItemIcon>
                    <ExitToApp fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content - top padding clears fixed AppBar so content is never hidden */}
        <Box
          sx={{
            paddingTop: { xs: '56px', sm: '64px' },
            px: { xs: 1.5, sm: 2, md: 3 },
            pb: { xs: 1.5, sm: 2, md: 3 },
            minHeight: '100vh',
            backgroundColor: 'grey.50',
            overflowX: 'hidden',
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {teacherTabs.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflowX: 'auto',
                bgcolor: 'background.paper',
              }}
            >
              <Tabs
                value={activeTeacherTab}
                onChange={(event, value) => navigate(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  minHeight: 44,
                  px: 1,
                  '& .MuiTab-root': {
                    minHeight: 44,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                  },
                }}
              >
                {teacherTabs.map((tab) => (
                  <Tab key={tab.path} value={tab.path} label={tab.label} />
                ))}
              </Tabs>
            </Paper>
          )}
          {children}
        </Box>
      </Box>

      {/* Support Dialog */}
      <SupportDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        presetEmail={user?.email || ''}
      />

      {/* Invite Dialog */}
      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

      {/* Nudge Dialog */}
      <NudgeDialog
        open={nudgeOpen}
        onClose={() => setNudgeOpen(false)}
      />
    </Box>
  );
};

export default TeacherLayout;
